import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { DateRange } from 'react-day-picker';
import {
  presetOptions,
  type PresetPeriod,
} from '@/components/ui/date-picker/presets';

const STORAGE_KEY = 'finance_date_range';
const EXPIRY_MS = 7 * 60 * 60 * 1000; // 7 hours

interface StoredDateRange {
  preset: PresetPeriod;
  from?: string;
  to?: string;
  timestamp: number;
}

interface DateRangeContextType {
  dateRange: DateRange | undefined;
  selectedPreset: PresetPeriod;
  setDateRange: (range: DateRange | undefined, preset: PresetPeriod) => void;
  startDate: string | undefined;
  endDate: string | undefined;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(
  undefined,
);

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context)
    throw new Error('useDateRange must be used within a DateRangeProvider');
  return context;
};

const formatLocalDate = (date: Date | undefined): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function getDefault(): { preset: PresetPeriod; range: DateRange | undefined } {
  const preset = presetOptions.find((p) => p.value === 'this_month');
  return { preset: 'this_month', range: preset?.getRange() };
}

function loadFromStorage(): {
  preset: PresetPeriod;
  range: DateRange | undefined;
} {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return getDefault();

    const parsed: StoredDateRange = JSON.parse(stored);
    if (Date.now() - parsed.timestamp > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return getDefault();
    }

    // "All time" has no expiry — always restore it
    if (parsed.preset === 'all_time') {
      return { preset: 'all_time', range: undefined };
    }

    // For named presets, recalculate the range so it stays current
    if (parsed.preset !== 'custom') {
      const presetOption = presetOptions.find((p) => p.value === parsed.preset);
      if (presetOption)
        return { preset: parsed.preset, range: presetOption.getRange() };
    }

    // For custom ranges, restore from stored ISO strings
    const range: DateRange = {
      from: parsed.from ? new Date(parsed.from) : undefined,
      to: parsed.to ? new Date(parsed.to) : undefined,
    };
    return { preset: parsed.preset, range };
  } catch {
    return getDefault();
  }
}

const isIsoDate = (v: string | null): v is string =>
  !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** `yyyy-mm-dd` as local midnight, the way the picker builds its dates. */
const parseLocalDate = (v: string): Date => {
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Pages whose links carry a period the shared range should take on. */
const PERIOD_PAGES = ['/dashboard/finance', '/dashboard/reports'];

export const DateRangeProvider = ({ children }: { children: ReactNode }) => {
  const initial = loadFromStorage();
  const [dateRange, setDateRangeState] = useState<DateRange | undefined>(
    initial.range,
  );
  const [selectedPreset, setSelectedPreset] = useState<PresetPeriod>(
    initial.preset,
  );

  const setDateRange = useCallback(
    (range: DateRange | undefined, preset: PresetPeriod) => {
      setDateRangeState(range);
      setSelectedPreset(preset);

      const toStore: StoredDateRange = {
        preset,
        from: range?.from?.toISOString(),
        to: range?.to?.toISOString(),
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    },
    [],
  );

  // A finance or report link that names a period is obeyed. Every
  // drill-through writes `startDate`/`endDate`, but nothing read them on the
  // pages driven by this range, so a link copied to a colleague, bookmarked,
  // or opened from a statement showed whatever period the reader last used,
  // under an address that said otherwise. The named period becomes the shared
  // one, so the next page opened from the menu agrees with this one too.
  const location = useLocation();
  useEffect(() => {
    if (!PERIOD_PAGES.some((p) => location.pathname.startsWith(p))) return;
    const params = new URLSearchParams(location.search);
    const start = params.get('startDate');
    const end = params.get('endDate');
    if (!isIsoDate(start) || !isIsoDate(end) || end < start) return;
    if (
      start === formatLocalDate(dateRange?.from) &&
      end === formatLocalDate(dateRange?.to)
    )
      return;
    // Named for the preset it matches, so the picker reads "Last month"
    // rather than two dates when that is what the link meant.
    const preset =
      presetOptions.find((p) => {
        const r = p.getRange();
        return (
          formatLocalDate(r?.from) === start && formatLocalDate(r?.to) === end
        );
      })?.value ?? 'custom';
    setDateRange(
      { from: parseLocalDate(start), to: parseLocalDate(end) },
      preset,
    );
    // Only a change of address should move the range; the picker moving it
    // must not be undone by an address that has not changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        selectedPreset,
        setDateRange,
        startDate: formatLocalDate(dateRange?.from),
        endDate: formatLocalDate(dateRange?.to),
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};
